import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Save, ArrowLeft, ArrowUp, ArrowDown } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

const inputCls = "w-full rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60";
const sel = "rounded-md border border-line bg-bg px-2.5 py-1.5 text-xs text-ink-primary focus:outline-none";
const STATUS_CLS = { published: "text-emerald-300", draft: "text-amber-300", archived: "text-ink-muted" };
const CATEGORIES_FALLBACK = ["Getting Started", "Troubleshooting", "Frequently Asked Questions"];

function useCategories() {
  const [cats, setCats] = useState(CATEGORIES_FALLBACK);
  useEffect(() => {
    api.get("/help/home").then((r) => setCats((r.data.categories || []).map((c) => c.name))).catch(() => {});
  }, []);
  return cats;
}

function ArticlesTab() {
  const cats = useCategories();
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // article object or "new"
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/help/articles").then((r) => setArticles(r.data.articles)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = async (a) => {
    if (a === "new") { setEditing({ title: "", category: cats[0], description: "", content: "", keywords: [], status: "draft" }); return; }
    const { data } = await api.get(`/admin/help/articles/${a.article_id}`);
    setEditing(data.article);
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...editing, keywords: Array.isArray(editing.keywords) ? editing.keywords : String(editing.keywords || "").split(",").map((k) => k.trim()).filter(Boolean) };
      if (editing.article_id) {
        const { article_id, views, helpful_yes, helpful_no, created_at, updated_at, author, read_minutes, ...body } = payload;
        await api.patch(`/admin/help/articles/${editing.article_id}`, body);
      } else {
        await api.post("/admin/help/articles", payload);
      }
      toast.success("Article saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const del = async (a) => {
    if (!await appConfirm(`Delete "${a.title}"?`)) return;
    try { await api.delete(`/admin/help/articles/${a.article_id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  if (editing) {
    const set = (k) => (v) => setEditing((x) => ({ ...x, [k]: v }));
    return (
      <div className="space-y-3 max-w-3xl" data-testid="article-editor">
        <button type="button" onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary">
          <ArrowLeft className="h-3.5 w-3.5" /> All articles
        </button>
        <input className={inputCls} value={editing.title} onChange={(e) => set("title")(e.target.value)} placeholder="Article title" data-testid="article-title-input" />
        <div className="grid sm:grid-cols-3 gap-2">
          <select className={inputCls} value={editing.category} onChange={(e) => set("category")(e.target.value)} data-testid="article-category-select">
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className={inputCls} value={editing.status} onChange={(e) => set("status")(e.target.value)} data-testid="article-status-select">
            <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
          </select>
          <input className={inputCls} value={editing.slug || ""} onChange={(e) => set("slug")(e.target.value)} placeholder="slug (auto)" />
        </div>
        <input className={inputCls} value={editing.description} onChange={(e) => set("description")(e.target.value)} placeholder="Short description" data-testid="article-desc-input" />
        <input className={inputCls} value={Array.isArray(editing.keywords) ? editing.keywords.join(", ") : editing.keywords}
          onChange={(e) => set("keywords")(e.target.value)} placeholder="Keywords, comma, separated" />
        <div className="grid sm:grid-cols-2 gap-2">
          <input className={inputCls} value={editing.featured_image || ""} onChange={(e) => set("featured_image")(e.target.value)} placeholder="Featured image URL (optional)" />
          <input className={inputCls} value={editing.video_url || ""} onChange={(e) => set("video_url")(e.target.value)} placeholder="Embedded video URL (optional)" />
        </div>
        <textarea className={`${inputCls} min-h-[300px] font-mono text-xs`} value={editing.content}
          onChange={(e) => set("content")(e.target.value)} data-testid="article-content-input"
          placeholder={"## Heading\nParagraph text with **bold**.\n1. Numbered step\n- Bullet\n> NOTE: callout\n> WARNING: careful\n> TIP: pro tip"} />
        <button type="button" onClick={save} disabled={busy} data-testid="article-save-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save article
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="articles-admin-list">
      <button type="button" onClick={() => openEdit("new")} data-testid="article-new-btn"
        className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-3.5 py-2 text-xs font-semibold text-black">
        <Plus className="h-3.5 w-3.5" /> New article
      </button>
      <div className="space-y-1.5">
        {articles.map((a) => (
          <div key={a.article_id} className="flex items-center gap-3 rounded-lg border border-line bg-bg-surface px-4 py-2.5"
            data-testid={`article-admin-row-${a.slug}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-ink-primary truncate">{a.title}</div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">
                {a.category} · <span className={STATUS_CLS[a.status]}>{a.status}</span> · {a.views} views ·
                <span className="text-emerald-300"> {a.helpful_yes}↑</span><span className="text-rose-300"> {a.helpful_no}↓</span>
              </div>
            </div>
            <button type="button" onClick={() => openEdit(a)} className="text-ink-muted hover:text-brand-amber" data-testid={`article-edit-${a.slug}`}><Pencil className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => del(a)} className="text-ink-muted hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqsTab() {
  const cats = useCategories();
  const [faqs, setFaqs] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/help/faqs").then((r) => setFaqs(r.data.faqs)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing.faq_id) {
        const { faq_id, order, updated_at, ...body } = editing;
        await api.patch(`/admin/help/faqs/${faq_id}`, body);
      } else await api.post("/admin/help/faqs", editing);
      toast.success("FAQ saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const move = async (i, dir) => {
    const next = [...faqs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setFaqs(next);
    await api.post("/admin/help/faqs/reorder", { faq_ids: next.map((f) => f.faq_id) });
  };
  const del = async (f) => {
    if (!await appConfirm("Delete this FAQ?")) return;
    await api.delete(`/admin/help/faqs/${f.faq_id}`); load();
  };
  return (
    <div className="space-y-3 max-w-3xl" data-testid="faqs-admin">
      {editing ? (
        <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-2">
          <input className={inputCls} value={editing.question} placeholder="Question" data-testid="faq-question-input"
            onChange={(e) => setEditing({ ...editing, question: e.target.value })} />
          <textarea className={`${inputCls} min-h-[90px]`} value={editing.answer} placeholder="Answer" data-testid="faq-answer-input"
            onChange={(e) => setEditing({ ...editing, answer: e.target.value })} />
          <div className="flex flex-wrap gap-2">
            <select className={sel} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className={sel} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="faq-status-select">
              <option value="published">Published</option><option value="draft">Unpublished</option>
            </select>
            <input className={`${sel} flex-1`} value={editing.related_slug || ""} placeholder="Related article slug (optional)"
              onChange={(e) => setEditing({ ...editing, related_slug: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={save} className="rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black" data-testid="faq-save-btn">Save FAQ</button>
            <button type="button" onClick={() => setEditing(null)} className="text-xs text-ink-muted hover:text-ink-primary">Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" data-testid="faq-new-btn"
          onClick={() => setEditing({ question: "", answer: "", category: cats[0], status: "published", related_slug: "" })}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-3.5 py-2 text-xs font-semibold text-black">
          <Plus className="h-3.5 w-3.5" /> New FAQ
        </button>
      )}
      <div className="space-y-1.5">
        {faqs.map((f, i) => (
          <div key={f.faq_id} className="flex items-center gap-2 rounded-lg border border-line bg-bg-surface px-4 py-2.5"
            data-testid={`faq-admin-row-${f.faq_id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-ink-primary truncate">{f.question}</div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">
                {f.category} · <span className={STATUS_CLS[f.status] || ""}>{f.status}</span>
              </div>
            </div>
            <button type="button" onClick={() => move(i, -1)} className="text-ink-muted hover:text-ink-primary"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => move(i, 1)} className="text-ink-muted hover:text-ink-primary"><ArrowDown className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => setEditing(f)} className="text-ink-muted hover:text-brand-amber"><Pencil className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => del(f)} className="text-ink-muted hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VideosTab() {
  const [videos, setVideos] = useState([]);
  const load = () => api.get("/admin/help/videos").then((r) => setVideos(r.data.videos)).catch(() => {});
  useEffect(() => { load(); }, []);
  const patch = async (v, body) => {
    try { await api.patch(`/admin/help/videos/${v.video_id}`, body); load(); toast.success("Video updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  return (
    <div className="space-y-1.5 max-w-3xl" data-testid="videos-admin">
      <p className="text-[11px] text-ink-muted mb-2">Paste real YouTube/Vimeo URLs when your tutorials are ready — placeholders show "coming soon" to users.</p>
      {videos.map((v) => (
        <div key={v.video_id} className="rounded-lg border border-line bg-bg-surface px-4 py-3" data-testid={`video-admin-row-${v.video_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-ink-primary flex-1 min-w-[180px]">{v.title}</span>
            <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">{v.difficulty}</span>
            <select className={sel} value={v.status} onChange={(e) => patch(v, { status: e.target.value })}>
              <option value="published">Published</option><option value="draft">Hidden</option>
            </select>
          </div>
          <div className="flex gap-2 mt-2">
            <input className={`${inputCls} !py-1.5 text-xs`} defaultValue={v.video_url} placeholder="Video URL (YouTube / Vimeo)…"
              data-testid={`video-url-input-${v.video_id}`}
              onBlur={(e) => e.target.value !== v.video_url && patch(v, { video_url: e.target.value.trim() })} />
            <input className={`${inputCls} !py-1.5 text-xs w-24 shrink-0`} defaultValue={v.duration} placeholder="8:32"
              onBlur={(e) => e.target.value !== v.duration && patch(v, { duration: e.target.value.trim() })} />
          </div>
        </div>
      ))}
    </div>
  );
}

function FeedbackTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/help/feedback").then((r) => setData(r.data)).catch(() => {}); }, []);
  const REASON_LABELS = { unclear: "Unclear", steps_failed: "Steps didn't work", outdated: "Outdated", different_issue: "Different issue", other: "Other" };
  return (
    <div className="space-y-4 max-w-3xl" data-testid="feedback-admin">
      {data?.poor_rated?.length > 0 && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
          <div className="font-mono text-[10px] uppercase tracking-wider text-rose-300 mb-2">Articles needing attention</div>
          {data.poor_rated.map((a) => (
            <div key={a.slug} className="flex justify-between py-1 text-xs">
              <span className="text-ink-secondary">{a.title}</span>
              <span className="font-mono"><span className="text-emerald-300">{a.helpful_yes}↑</span> <span className="text-rose-300">{a.helpful_no}↓</span></span>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-1.5">
        {(data?.feedback || []).map((f, i) => (
          <div key={i} className="rounded-lg border border-line bg-bg-surface px-4 py-2.5 text-xs">
            <div className="flex items-center gap-2">
              <span className={f.helpful ? "text-emerald-300" : "text-rose-300"}>{f.helpful ? "Helpful" : "Not helpful"}</span>
              <span className="text-ink-muted">· {f.slug}</span>
              {f.reason && <span className="text-amber-300">· {REASON_LABELS[f.reason] || f.reason}</span>}
              <span className="ml-auto font-mono text-[9px] text-ink-muted">{new Date(f.created_at).toLocaleString()}</span>
            </div>
            {f.comment && <div className="text-ink-secondary mt-1">"{f.comment}"</div>}
          </div>
        ))}
        {data && data.feedback.length === 0 && <div className="text-sm text-ink-muted">No feedback yet.</div>}
      </div>
    </div>
  );
}

export default function AdminHelpContent() {
  return (
    <div className="space-y-6" data-testid="admin-help-content-page">
      <PageHeader eyebrow="// ADMIN · HELP CENTER" title="Help Content"
        description="Manage help articles, FAQs, video tutorials, and reader feedback." />
      <Tabs defaultValue="articles">
        <TabsList className="bg-bg-surface border border-line">
          <TabsTrigger value="articles" data-testid="tab-articles">Articles</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="tab-faqs">FAQs</TabsTrigger>
          <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
        </TabsList>
        <TabsContent value="articles" className="mt-4"><ArticlesTab /></TabsContent>
        <TabsContent value="faqs" className="mt-4"><FaqsTab /></TabsContent>
        <TabsContent value="videos" className="mt-4"><VideosTab /></TabsContent>
        <TabsContent value="feedback" className="mt-4"><FeedbackTab /></TabsContent>
      </Tabs>
    </div>
  );
}
